
import { initializeApp, getApps, getApp, type FirebaseApp } from 'firebase/app';
import { getAuth, type Auth } from 'firebase/auth';
import { getFirestore, type Firestore } from 'firebase/firestore';
// DO NOT import { getRemoteConfig, type RemoteConfig } from 'firebase/remote-config';

const firebaseConfig = {
  apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
  authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
  storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID,
};

const isServerContext = typeof window === 'undefined';
const contextType = isServerContext ? "SERVER" : "CLIENT";
const logPrefix = `[FirebaseInit - ${contextType}]`;

let app: FirebaseApp;
let authInstance: Auth | null = null;
let dbInstance: Firestore | null = null;

let appInitialized = false;
let authInitialized = false;
let firestoreInitialized = false;

// --- App Initialization ---
if (getApps().length === 0) {
  if (firebaseConfig.apiKey && firebaseConfig.authDomain && firebaseConfig.projectId) {
    try {
      app = initializeApp(firebaseConfig);
      appInitialized = true;
    } catch (error: any) {
      console.error(`${logPrefix} CRITICAL ERROR during Firebase App initializeApp():`, error.message, error.stack);
      // @ts-ignore Assign a clearly non-functional placeholder
      app = { name: "dummy-app-init-error", options: {}, problem: "initializeApp_threw_exception", errorMessage: error.message, _isDeleted: true } as FirebaseApp;
    }
  } else {
    console.warn(`${logPrefix} Firebase core config (apiKey, authDomain, projectId) is INCOMPLETE. Firebase services will be placeholders.`);
    // @ts-ignore
    app = { name: "dummy-app-config-error", options: {}, problem: "config_incomplete", _isDeleted: true } as FirebaseApp;
  }
} else {
  app = getApp();
  appInitialized = true;
}

// --- Auth Initialization ---
// @ts-ignore
if (app && !app._isDeleted && app.name && !app.name.startsWith("dummy-app-")) {
  try {
    authInstance = getAuth(app);
    authInitialized = true;
  } catch (e: any) {
    console.error(`${logPrefix} ERROR obtaining Firebase Auth for app '${app.name}':`, e.message, e.stack);
  }
} else if (appInitialized) {
  console.warn(`${logPrefix} Firebase App was not properly initialized. Firebase Auth will be a non-functional placeholder.`);
}

// --- Firestore Initialization ---
// @ts-ignore
if (app && !app._isDeleted && app.name && !app.name.startsWith("dummy-app-")) {
  try {
    const tempDbInstance = getFirestore(app);
    if (tempDbInstance && (tempDbInstance as any).type === 'firestore') {
      dbInstance = tempDbInstance;
      firestoreInitialized = true;
    } else {
      console.error(`${logPrefix} CRITICAL ERROR: getFirestore(app) for app '${app.name}' did not return a valid Firestore instance. Firestore will NOT WORK.`);
    }
  } catch (e: any) {
    console.error(`${logPrefix} CRITICAL ERROR obtaining Firestore for app '${app.name}':`, e.message, e.stack);
  }
} else if (appInitialized) {
  console.warn(`${logPrefix} Firebase App was not properly initialized. Firebase Firestore will be a non-functional placeholder.`);
}

// --- Remote Config Initialization IS COMPLETELY REMOVED FROM THIS FILE ---

if (appInitialized) {
    console.log(`${logPrefix} Firebase SDKs initialized (App: ${app.name || 'N/A'}, Auth: ${authInitialized ? 'OK' : 'FAIL'}, Firestore: ${firestoreInitialized ? 'OK' : 'FAIL'}). Project ID: ${app.options?.projectId || firebaseConfig.projectId || 'N/A'}`);
} else if (!(firebaseConfig.apiKey && firebaseConfig.authDomain && firebaseConfig.projectId)) {
    // Already warned about incomplete config
} else {
    console.error(`${logPrefix} Firebase App failed to initialize for an unknown reason, though config seemed present.`);
}

const finalAuth = authInstance || ({} as Auth);
const finalDb = dbInstance || ({} as Firestore);

export { app, finalAuth as auth, finalDb as db };
